import { Component, computed, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import * as fromAuth from '../../auth';
import * as fromLayout from '../../layout';
import { TopBarMobileResponsive } from '../components/top-bar.mobile.responsive';
import * as fromShared from '../shared';
import { teammatesFeature, TeammatesState } from './store';
import { getTeammateVisualFromParts } from './teammate-avatar.config';
import { TeammateVisualComponent } from './teammate-visual.component';

@Component({
  imports: [
    TopBarMobileResponsive,
    TranslateModule,
    fromSeriousSystem.UseArrowIconDirective,
    TeammateVisualComponent,
  ],
  template: `
    <div class="bg-gradient-e-vertical h-svh w-full flex flex-col relative">
      @if (isMobile()) {
        <squadbox-top-bar-mobile
          class="bg-shades-white"
          (chevronLeftButtonClicked)="handleChevronLeftButtonCTA()"
        >
        </squadbox-top-bar-mobile>
      }

      <div class="flex-1 overflow-y-auto hide-scrollbar">
        <div
          class="
            fade-in-up-subtle
            min-h-full
            flex flex-col items-center justify-center
            px-4 tablet-portrait:px-6
            py-16
          "
        >
          <div class="flex flex-col items-center gap-1 text-center">
            <p class="typo-h6 leading-8 text-neutral-500">
              {{ greeting() }}
            </p>
            <h1 class="typo-h4 leading-10 text-neutral-900">
              {{ 'TEAMMATE_LANDING.TITLE' | translate }}
            </h1>
          </div>

          <div
            class="
              mt-10
              grid w-full max-w-6xl
              grid-cols-[repeat(auto-fit,256px)]
              justify-center gap-4
            "
          >
            @for (card of teammateCards(); track card.teammate.id) {
              <button
                class="
                  group
                  relative
                  flex h-74 w-64 flex-col overflow-hidden
                  rounded-2xl border border-neutral-200 bg-shades-white
                  text-left shadow-sm transition-shadow duration-200
                  hover:shadow
                "
                (click)="handleClickTeammateCTA(card.teammate.slug)"
                (pointerenter)="hoveredTeammateId.set(card.teammate.id)"
                (pointerleave)="hoveredTeammateId.set(null)"
              >
                <!-- Hover is driven from the CARD, not the avatar: the avatar
                     only occupies the top of the card, so its own pointer
                     handlers left the rest of the card inert. Supplying the
                     hovered input makes the avatar defer to us (see its
                     playHoverAnimation guard) — the same pattern
                     navigation-teammate.component.ts uses for the nav row. -->
                <squadbox-teammate-visual
                  class="h-38"
                  [visual]="card.visual"
                  presentation="card-header"
                  [hovered]="hoveredTeammateId() === card.teammate.id"
                />

                <div class="flex grow flex-col justify-between gap-4 p-6">
                  <div class="flex flex-col">
                    <p class="typo-p1 leading-6 font-semibold text-neutral-700">
                      {{ card.teammate.name }}
                    </p>
                    <p class="typo-p2 leading-6 text-neutral-500">
                      {{ card.teammate.title }}
                    </p>
                  </div>

                  <div class="flex items-end justify-between gap-4">
                    <div
                      class="
                        inline-flex items-center rounded border border-neutral-200
                        px-2 py-1 typo-caption leading-4 font-semibold
                        text-neutral-700
                      "
                    >
                      {{
                        'TEAMMATE_LANDING.EXPERTISE_COUNT'
                          | translate
                            : { count: card.teammate.assistants.length }
                      }}
                    </div>

                    <div class="hidden tablet-landscape:flex card-arrow-action">
                      <svg sdUseArrowIcon="arrow-right"></svg>
                    </div>
                  </div>
                </div>
              </button>
            }
          </div>
        </div>
      </div>

      <div
        class="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-g-vertical"
      ></div>
    </div>
  `,
})
export class TeammatesLandingPage {
  private readonly layoutStore = inject(Store<fromLayout.LayoutState>);
  private readonly authStore = inject(Store<fromAuth.AuthState>);
  private readonly teammatesStore = inject<Store<TeammatesState>>(Store);
  private readonly routerService = inject(fromShared.RouterService);
  private readonly mobileNavigation = inject(
    fromShared.MobileNavigationService,
  );
  private readonly translateService = inject(TranslateService);

  public readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );
  public readonly teammates = this.teammatesStore.selectSignal(
    teammatesFeature.selectAllWithAssistants,
  );

  /**
   * Which card the pointer is currently over, so the whole card drives the
   * avatar's hover animation rather than just the avatar's own bounds.
   */
  public readonly hoveredTeammateId = signal<string | null>(null);

  /**
   * Teammates with their resolved visual precomputed.
   *
   * This page is CheckAlways, so binding the resolver straight from the
   * template called it on every change-detection pass. That returns a fresh
   * object literal each time, so the visual input signal was rewritten on every
   * tick, which re-dirtied the avatar's own initialization effect — and that
   * effect calls `cleanupHoverAnimation()` + `gsap.set(...,
   * FULL_BODY_REGULAR_STATE)`, killing any in-flight hover animation.
   * Precomputing keeps the reference stable so `Object.is` holds.
   *
   * SB-3519 widened this from the avatar config to the visual union. The union
   * is built with spreads, so it is exactly as reference-unstable as before and
   * the seam adds a component to the input chain — this memoization is more
   * load-bearing after the change, not less.
   */
  public readonly teammateCards = computed(() =>
    this.teammates().map((teammate) => ({
      teammate,
      visual: getTeammateVisualFromParts(teammate),
    })),
  );
  private readonly currentUser = this.authStore.selectSignal(
    fromAuth.authFeature.selectUser,
  );

  public readonly greeting = computed(() => {
    const firstName = this.currentUser()?.firstName;

    return firstName
      ? (this.translateService.instant('TEAMMATE_LANDING.GREETING', {
          firstName,
        }) as string)
      : (this.translateService.instant(
          'TEAMMATE_LANDING.GREETING_FALLBACK',
        ) as string);
  });

  public async handleChevronLeftButtonCTA(): Promise<void> {
    await this.mobileNavigation.open();
  }

  public async handleClickTeammateCTA(slug: string): Promise<void> {
    await this.routerService.navigateToTeammate(slug);
  }
}
